import { type IPostCandidatesSearch, plurialize } from "@api";
import type { ObjectId } from "@bbj-types/utils";
import { Stack, Typography } from "@mui/material";
import { GridVirtualizedSelect } from "@styleguide";
import { type Dispatch, type MutableRefObject, type SetStateAction, useEffect } from "react";
import { CandidateItem } from "./candidateItem";

export interface IAllCandidatesGridProps {
  selectedCandidates: IPostCandidatesSearch["data"];
  disabledCandidates?: { _id: ObjectId; tooltip?: string }[];
  hasFilters: boolean;
  setSelectedCandidates: (candidates: IPostCandidatesSearch["data"]) => void;
  findCandidateWorkshop: () => IPostCandidatesSearch;
  setTotal: Dispatch<SetStateAction<number>>;
  scrollerRef: MutableRefObject<HTMLDivElement | null>;
}

export const AllCandidatesGrid = ({
  selectedCandidates,
  disabledCandidates,
  hasFilters,
  setSelectedCandidates,
  findCandidateWorkshop,
  setTotal,
  scrollerRef,
}: IAllCandidatesGridProps) => {
  const { data: candidates, total } = findCandidateWorkshop();

  // biome-ignore lint/correctness/useExhaustiveDependencies: only update when total change
  useEffect(() => setTotal(total), [total]);

  return (
    <GridVirtualizedSelect
      data={candidates}
      selectedItems={selectedCandidates}
      disabledItems={disabledCandidates}
      customScrollParent={scrollerRef.current ?? undefined}
      getId={(item: Pick<IPostCandidatesSearch["data"][number], "_id">) => `${item._id}`}
      emptyContent={() => (
        <Typography variant="body1">
          {hasFilters
            ? "Aucun candidat ne correspond à votre sélection de filtres."
            : "Aucun candidat dans votre environnement pour le moment."}
        </Typography>
      )}
      onChange={(selectedItems) => setSelectedCandidates(selectedItems)}
      itemContent={(_, candidate) => <CandidateItem candidate={candidate} />}
      headerContent={() => (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body1Bold">
            Résultat de la recherche : {candidates.length} {plurialize("candidat", candidates.length)}
          </Typography>
        </Stack>
      )}
    />
  );
};
